import React from 'react';
import { Link } from 'react-router-dom';
import { siteConfig } from '../data/mock';
import { Heart, MapPin, Mail } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-b from-grape-800 to-grape-900 text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-lavender-800/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-violet-800/10 rounded-full blur-3xl" />
      
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid md:grid-cols-3 gap-12">
          {/* Brand Section */}
          <div className="space-y-6">
            <div className="relative inline-block">
              <div className="absolute -inset-2 bg-white/10 rounded-xl blur-sm" />
              <img
                src="https://customer-assets.emergentagent.com/job_54544642-256c-49a8-b79e-1e945d33b744/artifacts/vlf8i0qw_0.jpg"
                alt="Hem of Grace Events - Florida Luxury Wedding and Event Planner"
                className="relative h-20 w-auto rounded-lg shadow-lg"
              />
            </div>
            <p className="text-white/80 text-sm leading-relaxed max-w-xs" style={{ fontFamily: "'Lato', sans-serif" }}>
              {siteConfig.tagline} Creating graceful events in Florida with personalized design and seamless wedding coordination.
            </p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4
              className="text-xl mb-6 text-white"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Quick Links
            </h4>
            <nav className="space-y-3" aria-label="Footer navigation">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="block text-white/70 hover:text-lavender-300 hover:translate-x-1 transition-all duration-300 text-sm"
                  style={{ fontFamily: "'Lato', sans-serif" }}
                >
                  {link.name}
                </a>
              ))}
            </nav>
          </div>
          
          {/* Contact Info */}
          <div>
            <h4
              className="text-xl mb-6 text-white"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Get In Touch
            </h4>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-lavender-300" />
                </div>
                <span className="text-white/70 text-sm" style={{ fontFamily: "'Lato', sans-serif" }}>
                  {siteConfig.location}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-lavender-300" />
                </div>
                <a
                  href={`mailto:${siteConfig.email}`}
                  className="text-white/70 hover:text-lavender-300 transition-colors text-sm"
                  style={{ fontFamily: "'Lato', sans-serif" }}
                >
                  {siteConfig.email}
                </a>
              </div>
              
              {/* Social Icons */}
              <div className="pt-4">
                <p className="text-white/60 text-xs mb-3" style={{ fontFamily: "'Lato', sans-serif" }}>
                  Connect with Hem of Grace Events
                </p>
                <div className="flex space-x-3">
                  <a
                    href={siteConfig.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-white/10 hover:bg-lavender-500/40 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                    aria-label="Follow Hem of Grace Events on Instagram"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a
                    href={siteConfig.social.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-white/10 hover:bg-lavender-500/40 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                    aria-label="Follow Hem of Grace Events on TikTok"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                  </a>
                  <a
                    href={siteConfig.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-white/10 hover:bg-lavender-500/40 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                    aria-label="Follow Hem of Grace Events on Facebook"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="border-t border-white/10 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
            <p className="text-white/50 text-sm" style={{ fontFamily: "'Lato', sans-serif" }}>
              © {currentYear} {siteConfig.businessName}. All rights reserved.
            </p>
            <div className="flex items-center space-x-4">
              <Link 
                to="/privacy-policy" 
                className="text-white/50 hover:text-lavender-300 text-sm transition-colors relative z-20"
                style={{ fontFamily: "'Lato', sans-serif" }}
              >
                Privacy Policy
              </Link>
              <span className="text-white/30">|</span>
              <p className="text-white/50 text-sm flex items-center" style={{ fontFamily: "'Lato', sans-serif" }}>
                Made with <Heart className="w-4 h-4 mx-1.5 text-lavender-400" /> in Florida
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
